import { useCallback, useMemo, useState } from 'react';
import {
  Box, Flex, Text, Button, Spinner, Badge, SimpleGrid, Image, useToast,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchRelatedOverride, setRelatedOverride, relatedOverrideQueryKey,
  type AudioPlaylistDto,
} from '../../../api/playlistCurationApi';
import { fetchCuratedPlaylists, type CuratedPlaylistListItemDto } from '../../../api/libraryApi';
import { ApiError } from '../../../services/apiClient';
import { SortablePlaylistList, type SortableRow } from './SortablePlaylistList';
import { PlaylistPickerModal } from './PlaylistPickerModal';

interface RelatedOverrideEditorProps {
  /** The source library playlist whose related list is being edited. */
  playlistId: string;
}

function toRow(p: AudioPlaylistDto): SortableRow {
  return { id: p.id, title: p.name, subtitle: p.targetUseCase ?? null, thumbnailUrl: p.thumbnailUrl };
}

function curatedToRow(p: CuratedPlaylistListItemDto): SortableRow {
  return { id: p.id, title: p.name, subtitle: null, thumbnailUrl: p.thumbnailUrl };
}

/**
 * Related-override editor (feature 017 US4). Shows the current admin override as a
 * dnd-kit orderable list plus a read-only "algorithmic preview" of what the matching
 * algorithm would return. Add/remove/reorder edit a working set; Save persists it via
 * PUT related. "Clear (use algorithm)" saves an empty array, reverting to the algorithm.
 */
export function RelatedOverrideEditor({ playlistId }: RelatedOverrideEditorProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const toast = useToast();
  const [pickerOpen, setPickerOpen] = useState(false);
  // Local working set of override rows (ordered). null = not yet edited (mirrors server state).
  const [working, setWorking] = useState<SortableRow[] | null>(null);

  const { data: state, isLoading } = useQuery({
    queryKey: relatedOverrideQueryKey(playlistId),
    queryFn: () => fetchRelatedOverride(playlistId),
    enabled: Boolean(playlistId),
  });

  // Every published library playlist — the pool the picker can add from. Shares the
  // picker's query key so it's fetched once and cached. Needed so a freshly-picked
  // playlist (one that isn't already in the override or the algorithmic preview) can be
  // resolved into a renderable row instead of being silently dropped.
  const { data: allPlaylists } = useQuery({
    queryKey: ['admin-curated-playlists-picker'],
    queryFn: fetchCuratedPlaylists,
  });

  // The effective override rows: local edits if present, otherwise the server state.
  const overrideRows: SortableRow[] = useMemo(
    () => working ?? (state?.override ?? []).map(toRow),
    [working, state?.override],
  );

  // A lookup of every playlist we can render a row for (all library playlists + preview +
  // override + working), so any picked/reordered id resolves. Richer DTOs win on conflict.
  const knownById = useMemo(() => {
    const map = new Map<string, SortableRow>();
    (allPlaylists ?? []).forEach((p) => map.set(p.id, curatedToRow(p)));
    (state?.algorithmicPreview ?? []).forEach((p) => map.set(p.id, toRow(p)));
    (state?.override ?? []).forEach((p) => map.set(p.id, toRow(p)));
    overrideRows.forEach((r) => map.set(r.id, r));
    return map;
  }, [allPlaylists, state, overrideRows]);

  const saveMutation = useMutation({
    mutationFn: (ids: string[]) => setRelatedOverride(playlistId, ids),
    onSuccess: (_res, ids) => {
      queryClient.invalidateQueries({ queryKey: relatedOverrideQueryKey(playlistId) });
      setWorking(null);
      toast({
        title: ids.length === 0
          ? t('admin.relatedOverride.cleared', 'Reverted to algorithmic related')
          : t('admin.relatedOverride.saved', 'Related override saved'),
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    },
    onError: (err: unknown) => {
      if (err instanceof ApiError && err.status === 422) {
        toast({ title: t('admin.relatedOverride.invalid', 'Invalid: no self-reference, duplicates, or non-library playlists'), status: 'error', duration: 4000, isClosable: true });
      } else {
        toast({ title: t('admin.relatedOverride.saveFailed', 'Failed to save related override'), status: 'error', duration: 4000, isClosable: true });
      }
    },
  });

  const handleReorder = useCallback((orderedIds: string[]) => {
    setWorking(orderedIds.map((oid) => knownById.get(oid)).filter((r): r is SortableRow => Boolean(r)));
  }, [knownById]);

  const handleRemove = useCallback((id: string) => {
    setWorking(overrideRows.filter((r) => r.id !== id));
  }, [overrideRows]);

  const handlePickerConfirm = useCallback((ids: string[]) => {
    // Exclude the source playlist itself; the backend rejects self-references.
    setWorking(
      ids
        .filter((pid) => pid !== playlistId)
        .map((pid) => knownById.get(pid))
        .filter((r): r is SortableRow => Boolean(r)),
    );
  }, [knownById, playlistId]);

  const dirty = working !== null;

  if (isLoading) return <Flex justify="center" p={8}><Spinner size="xl" /></Flex>;

  const isOverridden = state?.isOverridden ?? false;
  const preview = state?.algorithmicPreview ?? [];

  return (
    <Box data-testid="related-override-editor">
      <Flex justify="space-between" align="center" mb={3} flexWrap="wrap" gap={2}>
        <Flex align="center" gap={2}>
          <Text fontWeight="600">{t('admin.relatedOverride.title', 'Related playlists')}</Text>
          <Badge colorScheme={isOverridden ? 'purple' : 'gray'} data-testid="related-override-mode">
            {isOverridden
              ? t('admin.relatedOverride.overridden', 'Overridden')
              : t('admin.relatedOverride.algorithmic', 'Algorithmic')}
          </Badge>
        </Flex>
        <Flex gap={2}>
          <Button size="sm" colorScheme="blue" onClick={() => setPickerOpen(true)} data-testid="related-add">
            {t('admin.relatedOverride.add', 'Add playlists')}
          </Button>
          <Button
            size="sm"
            variant="outline"
            colorScheme="orange"
            onClick={() => saveMutation.mutate([])}
            isDisabled={(!isOverridden && overrideRows.length === 0) || saveMutation.isPending}
            data-testid="related-clear"
          >
            {t('admin.relatedOverride.clear', 'Clear (use algorithm)')}
          </Button>
          <Button
            size="sm"
            colorScheme="brand"
            onClick={() => saveMutation.mutate(overrideRows.map((r) => r.id))}
            isLoading={saveMutation.isPending}
            isDisabled={!dirty}
            data-testid="related-save"
          >
            {t('common.save', 'Save')}
          </Button>
        </Flex>
      </Flex>

      <Text fontSize="sm" color="gray.500" mb={2}>
        {t('admin.relatedOverride.overrideHint', 'When set, these playlists (in this order) replace the algorithm.')}
      </Text>
      <SortablePlaylistList
        rows={overrideRows}
        onReorder={handleReorder}
        onRemove={handleRemove}
        emptyLabel={t('admin.relatedOverride.noOverride', 'No override — the algorithm decides related playlists')}
        testIdPrefix="related"
      />

      {/* ── Algorithmic preview (read-only) ── */}
      <Box mt={6}>
        <Text fontWeight="600" mb={1}>{t('admin.relatedOverride.previewTitle', 'Algorithmic preview')}</Text>
        <Text fontSize="sm" color="gray.500" mb={3}>
          {t('admin.relatedOverride.previewHint', 'What the matching algorithm would return when no override is set.')}
        </Text>
        {preview.length === 0 ? (
          <Text fontSize="sm" color="gray.400" data-testid="related-preview-empty">
            {t('admin.relatedOverride.previewEmpty', 'No algorithmic matches')}
          </Text>
        ) : (
          <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={2} data-testid="related-preview-list">
            {preview.map((p) => (
              <Flex key={p.id} align="center" gap={2} px={3} py={2} borderRadius="10px" border="1px solid" borderColor="gray.100" data-testid={`related-preview-${p.id}`}>
                <Box w="32px" h="32px" flexShrink={0} borderRadius="6px" overflow="hidden" bg="gray.100">
                  {p.thumbnailUrl && <Image src={p.thumbnailUrl} alt={p.name} w="100%" h="100%" objectFit="cover" />}
                </Box>
                <Text fontSize="sm" noOfLines={1}>{p.name}</Text>
              </Flex>
            ))}
          </SimpleGrid>
        )}
      </Box>

      {pickerOpen && (
        <PlaylistPickerModal
          open={pickerOpen}
          onClose={() => setPickerOpen(false)}
          selectedIds={overrideRows.map((r) => r.id)}
          onConfirm={handlePickerConfirm}
        />
      )}
    </Box>
  );
}
